import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';
import { useEvents } from '../api/events';
import { I } from './Icons';

export function EventSwitcher() {
  const navigate = useNavigate();
  const location = useLocation();
  const { eventId } = useParams<{ eventId: string }>();
  const eventsQuery = useEvents();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    function handleMouseDown(event: MouseEvent) {
      if (!rootRef.current) return;
      if (!rootRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [open]);

  const events = eventsQuery.data ?? [];
  const current = events.find((e) => e.id === eventId);

  function pickEvent(nextEventId: string) {
    setOpen(false);
    if (nextEventId === eventId) return;
    const path = location.pathname;
    const match = path.match(/^\/events\/[^/]+(\/.*)?$/);
    const tail = match?.[1] ?? '/setup';
    navigate(`/events/${nextEventId}${tail}`);
  }

  return (
    <div ref={rootRef} style={{ position: 'relative' }}>
      <button
        type="button"
        className="event-switcher"
        onClick={() => setOpen((v) => !v)}
      >
        {current ? (
          <>
            <span className="live">{current.shortCode}</span>
            <span>{current.name}</span>
          </>
        ) : (
          <span>Select event</span>
        )}
        <I.chevDown />
      </button>
      {open && (
        <div
          role="menu"
          style={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            left: 0,
            minWidth: 240,
            background: 'var(--bg-surface)',
            border: '1px solid var(--border-1)',
            borderRadius: 12,
            boxShadow: 'var(--shadow-xl)',
            padding: 6,
            zIndex: 40,
          }}
        >
          {events.length === 0 && (
            <div style={{ padding: '10px 12px', fontSize: 13, color: 'var(--fg-3)' }}>
              No events
            </div>
          )}
          {events.map((event) => (
            <button
              key={event.id}
              type="button"
              role="menuitem"
              onClick={() => pickEvent(event.id)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                width: '100%',
                padding: '8px 10px',
                borderRadius: 8,
                background: event.id === eventId ? 'var(--oeg-purple-wash)' : 'transparent',
                color: 'var(--fg-1)',
                cursor: 'pointer',
                textAlign: 'left',
                fontFamily: 'var(--font-body)',
                fontSize: 13,
                border: 'none',
              }}
            >
              <span className="live">{event.shortCode}</span>
              <span>{event.name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
